import { TFunction } from "i18next";
import {
	ClassroomRuleRequest,
	CreateRulesLibraryListRequest,
	RulesLibraryValidationError,
	RuleType,
	RulesLibraryList,
	UpdateRulesLibraryListRequest,
	ValidateRuleRequest,
} from "@/api/rulesLibrary";
import { parseDomain } from "@/utils/urlUtils";
import { LOCKED_RULE_DEPENDENCY_CONFLICT_CODE, LOCKED_RULE_WARNING_CODES, QUICK_BLOCK_LIST_NAME } from "./constants";
import { AppliedList, List, Rule, RuleAction } from "./types";

export function isQuickBlockList(list: Pick<List, "title">): boolean {
	return list.title === QUICK_BLOCK_LIST_NAME;
}

export function isOwnAppliedList(list: Pick<AppliedList, "teacherId">, qoriaUserId: string | undefined): boolean {
	return qoriaUserId !== undefined && qoriaUserId !== "" && list.teacherId === qoriaUserId;
}

export function toRuleAction(action: RuleType): RuleAction {
	return action === "BLOCKED" ? "block" : "allow";
}

export function toRuleType(action: RuleAction): RuleType {
	return action === "block" ? "BLOCKED" : "ALLOWED";
}

export function compareByCreatedAtDesc(a: { createdAt: number }, b: { createdAt: number }): number {
	return b.createdAt - a.createdAt;
}

export function sortRulesLibraryLists(lists: RulesLibraryList[]): RulesLibraryList[] {
	return [...lists].sort(compareByCreatedAtDesc);
}

export function transformRulesLibraryListToList(list: RulesLibraryList): List {
	return {
		id: list.id,
		title: list.name,
		rules: [...list.classroomRules].sort(compareByCreatedAtDesc).map((rule) => ({
			id: rule.id,
			target: rule.target,
			action: toRuleAction(rule.action),
			type: rule.type,
		})),
	};
}

export function transformRulesLibraryLists(lists: RulesLibraryList[]): List[] {
	return sortRulesLibraryLists(lists).map(transformRulesLibraryListToList);
}

export function buildCreateRulesLibraryListRequest(list: Pick<List, "title" | "rules">): CreateRulesLibraryListRequest {
	return {
		name: list.title,
		classroomRules: list.rules.map((rule) => ({
			id: rule.id,
			target: rule.target,
			type: rule.type,
			action: toRuleType(rule.action),
		})),
	};
}

export function buildUpdateRulesLibraryListRequest(original: List, saved: List): UpdateRulesLibraryListRequest {
	const originalById = new Map(original.rules.map((rule) => [rule.id, rule]));
	const savedIds = new Set(saved.rules.map((rule) => rule.id));

	const updated = saved.rules
		.filter((rule) => {
			const existing = originalById.get(rule.id);
			return existing !== undefined && existing.action !== rule.action;
		})
		.map((rule) => ({ id: rule.id, action: toRuleType(rule.action) }));

	const deleted = original.rules.filter((rule) => !savedIds.has(rule.id)).map((rule) => rule.id);

	const added = saved.rules
		.filter((rule) => !originalById.has(rule.id))
		.map((rule) => ({
			id: rule.id,
			target: rule.target,
			type: rule.type,
			action: toRuleType(rule.action),
		}));

	return {
		name: saved.title,
		...(updated.length > 0 ? { updated } : {}),
		...(deleted.length > 0 ? { deleted } : {}),
		...(added.length > 0 ? { added } : {}),
	};
}

export function hasListChanges(original: List, current: Pick<List, "title" | "rules">): boolean {
	if (original.title.trim() !== current.title.trim()) {
		return true;
	}

	const { updated, deleted, added } = buildUpdateRulesLibraryListRequest(original, {
		...original,
		title: current.title,
		rules: current.rules,
	});

	return updated !== undefined || deleted !== undefined || added !== undefined;
}

export function hasUnsavedListChanges(original: List | undefined, current: Pick<List, "title" | "rules">): boolean {
	if (original === undefined) {
		return current.title.trim().length > 0 || current.rules.length > 0;
	}

	return hasListChanges(original, current);
}

export function isRulesLibraryValidationError(error: unknown): error is RulesLibraryValidationError {
	return (
		typeof error === "object" &&
		error !== null &&
		"name" in error &&
		error.name === "RulesLibraryValidationError" &&
		"errors" in error &&
		Array.isArray((error as RulesLibraryValidationError).errors)
	);
}

/**
 * True when another active rule conflicts with or overrides this one, so it currently has no
 * effect. `enabled` is deliberately ignored: a rule the teacher switched off already reads as Off.
 */
export function isRuleInert(rule: Pick<Rule, "conflictedBy" | "overriddenBy">): boolean {
	return Boolean(rule.conflictedBy ?? rule.overriddenBy);
}

export function isLockedRuleConflictError(error: unknown): boolean {
	return (
		isRulesLibraryValidationError(error) &&
		error.errors.length > 0 &&
		error.errors.every((item) => LOCKED_RULE_WARNING_CODES.has(item.code))
	);
}

/** Hostname-only label for URL rules, so pasted paths and query strings are not shown. */
export function formatWebsiteTarget(target: string): string {
	return parseDomain(target) ?? target;
}

export function toClassroomRuleRequest(rule: Rule): ClassroomRuleRequest {
	return {
		id: rule.id,
		target: rule.target,
		type: rule.type,
		action: toRuleType(rule.action),
	};
}

export function buildValidateRuleRequest(candidate: Rule, draftRules: Rule[]): ValidateRuleRequest {
	return {
		candidate: toClassroomRuleRequest(candidate),
		draft: draftRules.filter((rule) => rule.id !== candidate.id).map(toClassroomRuleRequest),
	};
}

function ruleLabel(rule: Rule | undefined, fallbackTarget: string, lookupName: (target: string) => string): string {
	const target = rule?.target ?? fallbackTarget;
	if (rule?.type === "URL") {
		return formatWebsiteTarget(target);
	}
	return lookupName(target);
}

export function getRulesLibraryValidationCopy(params: {
	error: RulesLibraryValidationError;
	candidate?: Rule;
	rules: Rule[];
	t: TFunction;
	lookupName: (target: string) => string;
	/** Where the rule is being saved; only changes the duplicate wording. */
	scope?: "list" | "class";
	/** Overrides the default "Rule cannot be created" title for list-level actions. Locked-rule warnings keep their own title. */
	title?: string;
}): { title: string; description: string; severity: "error" | "warning" } {
	const { error, candidate, rules, t, lookupName, scope = "list" } = params;
	const firstError = error.errors[0];
	const title = params.title ?? t("rulesLibrary.validation.cannotCreate");

	if (!firstError) {
		return { title, description: t("errors.unexpectedError"), severity: "error" };
	}

	if (LOCKED_RULE_WARNING_CODES.has(firstError.code)) {
		return {
			title: t("createPolicy.lockedRuleBannerTitle"),
			description:
				firstError.code === LOCKED_RULE_DEPENDENCY_CONFLICT_CODE
					? t("rulesLibrary.validation.lockedRuleDependencyConflict")
					: t("createPolicy.lockedRuleBannerMessage"),
			severity: "warning",
		};
	}

	const relatedRule = firstError.relatedRuleId ? rules.find((rule) => rule.id === firstError.relatedRuleId) : undefined;
	const candidateLabel = candidate ? (candidate.type === "URL" ? formatWebsiteTarget(candidate.target) : lookupName(candidate.target)) : "";
	const relatedLabel = ruleLabel(relatedRule, relatedRule?.target ?? "", lookupName);

	if (firstError.code === "DEPENDENCY_CONFLICT") {
		const allowedRule = candidate?.action === "allow" ? candidate : relatedRule;
		const requiredRule = candidate?.action === "block" ? candidate : relatedRule;
		return {
			title,
			description: t("rulesLibrary.validation.dependencyConflict", {
				allowed: allowedRule ? ruleLabel(allowedRule, allowedRule.target, lookupName) : candidateLabel || relatedLabel,
				required: requiredRule ? ruleLabel(requiredRule, requiredRule.target, lookupName) : relatedLabel || candidateLabel,
			}),
			severity: "error",
		};
	}

	if (firstError.code === "DUPLICATE") {
		const duplicate = relatedRule ?? candidate;
		const action = duplicate?.action === "allow" ? t("rulesLibrary.form.allow") : t("rulesLibrary.form.block");
		const duplicateLabel =
			duplicate?.type === "URL" ? formatWebsiteTarget(duplicate.target) : duplicate ? lookupName(duplicate.target) : candidateLabel;
		return {
			title,
			description: t(scope === "class" ? "rulesLibrary.validation.duplicateInClass" : "rulesLibrary.validation.duplicate", {
				action: action.toLowerCase(),
				target: duplicateLabel,
			}),
			severity: "error",
		};
	}

	if (firstError.code === "CONFLICT") {
		return {
			title,
			description: t("rulesLibrary.validation.conflict", {
				target: relatedLabel || candidateLabel,
			}),
			severity: "error",
		};
	}

	return {
		title,
		description: descriptionForErrorCode(firstError.code, t) ?? t("errors.unexpectedError"),
		severity: "error",
	};
}

function descriptionForErrorCode(code: string, t: TFunction): string | undefined {
	switch (code) {
		case "RULES_LIBRARY_LIST_DUPLICATE_NAME":
			return t("rulesLibrary.validation.duplicateListName");
		case "RULES_LIBRARY_LIST_LIMIT_REACHED":
			return t("rulesLibrary.validation.listLimitReached");
		case "RULES_LIBRARY_RULE_LIMIT_REACHED":
			return t("rulesLibrary.validation.ruleLimitReached");
		case "RULES_LIBRARY_LIST_NOT_FOUND":
			return t("rulesLibrary.validation.listNotFound");
		case "RULES_LIST_ALREADY_APPLIED":
			return t("rulesLibrary.validation.listAlreadyApplied");
		case "CLASSROOM_RULE_LIMIT_REACHED":
			return t("rulesLibrary.validation.classroomRuleLimitReached");
		case "RULES_LIBRARY_LIST_EMPTY_NAME":
			return t("rulesLibrary.validation.emptyListName");
		case "IN_CLASS_RULE_NOT_FOUND":
			return t("rulesLibrary.validation.inClassRuleNotFound");
		case "RULES_LIBRARY_LIST_NOT_APPLIED":
			return t("rulesLibrary.validation.listNotApplied");
		case "INVALID_REQUEST":
			return t("rulesLibrary.validation.invalidRequest");
		case "INTERNAL_ERROR":
			return t("rulesLibrary.validation.internalError");
		default:
			return undefined;
	}
}

export interface RuleFaviconLookup {
	getFavicon: (id: string) => string;
	findAppControlFavicon: (id: string) => string | undefined;
}

/** Resolves the image URL for a signature or app-control rule. Website-only rules have no favicon. */
export function faviconUrlForRule(rule: { target: string; type: string }, lookup: RuleFaviconLookup): string | undefined {
	if (rule.type === "APP_CONTROL") {
		const url = lookup.findAppControlFavicon(rule.target);
		return url ? url : undefined;
	}
	if (rule.type === "SIGNATURE") {
		const url = lookup.getFavicon(rule.target);
		return url ? url : undefined;
	}
	return undefined;
}

/** Unique favicon URLs for the rules the drawer is about to show (lists, in-class, suggestions). */
export function collectRuleFaviconUrls(rules: Array<{ target: string; type: string }>, lookup: RuleFaviconLookup): string[] {
	const urls = new Set<string>();
	for (const rule of rules) {
		const url = faviconUrlForRule(rule, lookup);
		if (url !== undefined) {
			urls.add(url);
		}
	}
	return [...urls];
}

/** Warms the browser HTTP cache so row `<img>` tags can paint without a round trip. */
export function preloadFavicons(urls: string[]): void {
	for (const url of urls) {
		const image = new Image();
		image.src = url;
	}
}
